// Original and English text of a clause, definition or snippet from a grid code that is not in English: a segmented control Original · English · Side by side, remembered per viewer in localStorage (guarded) and shared by every instance, a language badge and the permanent note that the English is a machine translation and the original is binding; English or untranslated text renders exactly as the plain original.
import { LanguagesIcon } from "lucide-react";
import { useSyncExternalStore, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/cn";
import { STORAGE_KEYS } from "@/lib/storage";

export type BilingualMode = "original" | "english" | "side-by-side";

export interface BilingualTextProps {
  original: string;
  english: string | null;
  language: string;
  mode?: BilingualMode;
  renderOriginal?: (text: string) => ReactNode;
  variant?: "full" | "snippet";
  className?: string;
}

export const BILINGUAL_STORAGE_KEY = STORAGE_KEYS.bilingual;
export const MACHINE_TRANSLATION_NOTE =
  "English is a machine translation (Claude Sonnet 5.5). The original text is legally binding.";

const MODES: readonly BilingualMode[] = ["original", "english", "side-by-side"];
const MODE_LABELS: Record<BilingualMode, string> = {
  original: "Original",
  english: "English",
  "side-by-side": "Side by side",
};
const DEFAULT_MODE: BilingualMode = "original";
const listeners = new Set<() => void>();
let fallback: BilingualMode = DEFAULT_MODE;
let persisted = true;

function isMode(value: string | null): value is BilingualMode {
  return value !== null && (MODES as readonly string[]).includes(value);
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function readMode(): BilingualMode {
  const store = storage();
  if (!persisted || store === null) {
    return fallback;
  }
  try {
    const value = store.getItem(BILINGUAL_STORAGE_KEY);
    return isMode(value) ? value : DEFAULT_MODE;
  } catch {
    return fallback;
  }
}

export function setBilingualMode(mode: BilingualMode): void {
  fallback = mode;
  try {
    storage()?.setItem(BILINGUAL_STORAGE_KEY, mode);
  } catch {
    persisted = false;
  }
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === BILINGUAL_STORAGE_KEY) {
      listener();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function useBilingualMode(): [BilingualMode, (mode: BilingualMode) => void] {
  const mode = useSyncExternalStore(subscribe, readMode, () => DEFAULT_MODE);
  return [mode, setBilingualMode];
}

export function languageName(code: string): string {
  const normalized = code.trim().toLowerCase().split(/[-_]/u)[0] ?? "";
  try {
    return new Intl.DisplayNames(["en"], { type: "language" }).of(normalized) ?? normalized.toUpperCase();
  } catch {
    return normalized.toUpperCase();
  }
}

export function isEnglish(code: string): boolean {
  return code.trim().toLowerCase().split(/[-_]/u)[0] === "en";
}

export function hasTranslation(english: string | null | undefined, language: string): english is string {
  return english !== null && english !== undefined && english.trim().length > 0 && !isEnglish(language);
}

export function LanguageBadge({ language, className }: { language: string; className?: string }) {
  const code = (language.trim().toLowerCase().split(/[-_]/u)[0] ?? "").toUpperCase();
  return (
    <Badge variant="neutral" className={className} title={`Original in ${languageName(language)}`}>
      <span className="font-mono text-label font-semibold">{code}</span>
      {languageName(language)}
    </Badge>
  );
}

export function MachineTranslationNote({ className }: { className?: string }) {
  return (
    <p className={cn("m-0 flex items-start gap-1.5 text-xs text-muted-foreground", className)}>
      <LanguagesIcon aria-hidden className="mt-px size-3.5 shrink-0" />
      {MACHINE_TRANSLATION_NOTE}
    </p>
  );
}

function OriginalText({
  original,
  language,
  renderOriginal,
}: {
  original: string;
  language: string;
  renderOriginal: ((text: string) => ReactNode) | undefined;
}) {
  return <div lang={language}>{renderOriginal ? renderOriginal(original) : original}</div>;
}

function EnglishText({ english }: { english: string }) {
  return (
    <div lang="en" className="whitespace-pre-line text-ink-2">
      {english}
    </div>
  );
}

function Snippet({
  original,
  english,
  language,
  mode,
  className,
}: {
  original: string;
  english: string;
  language: string;
  mode: BilingualMode;
  className: string | undefined;
}) {
  const code = (language.trim().toLowerCase().split(/[-_]/u)[0] ?? "").toUpperCase();
  return (
    <span className={cn("flex min-w-0 flex-col gap-0.5", className)} data-slot="bilingual-snippet">
      {mode === "english" ? null : (
        <span lang={language} className="min-w-0">
          <span className="mr-1 font-mono text-label font-semibold text-muted-foreground">{code}</span>
          {original}
        </span>
      )}
      {mode === "original" ? null : (
        <span lang="en" className="min-w-0 text-ink-2" title={MACHINE_TRANSLATION_NOTE}>
          <span className="mr-1 font-mono text-label font-semibold text-muted-foreground">EN</span>
          {english}
          <span className="sr-only"> ({MACHINE_TRANSLATION_NOTE})</span>
        </span>
      )}
    </span>
  );
}

export function BilingualText({
  original,
  english,
  language,
  mode: forced,
  renderOriginal,
  variant = "full",
  className,
}: BilingualTextProps) {
  const [stored, setMode] = useBilingualMode();
  if (!hasTranslation(english, language)) {
    return <>{renderOriginal ? renderOriginal(original) : original}</>;
  }
  const mode = forced ?? stored;
  if (variant === "snippet") {
    return (
      <Snippet original={original} english={english} language={language} mode={mode} className={className} />
    );
  }
  const name = languageName(language);
  return (
    <div className={cn("flex flex-col gap-3", className)} data-slot="bilingual">
      <div className="flex flex-wrap items-center gap-2">
        <LanguageBadge language={language} />
        {forced === undefined ? (
          <ToggleGroup
            type="single"
            variant="seg"
            aria-label="Text language"
            value={mode}
            onValueChange={(value) => {
              if (isMode(value)) {
                setMode(value);
              }
            }}
          >
            {MODES.map((item) => (
              <ToggleGroupItem key={item} value={item}>
                {MODE_LABELS[item]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        ) : null}
      </div>
      {mode === "side-by-side" ? (
        <div className="grid grid-cols-1 gap-x-6 gap-y-3 lg:grid-cols-2">
          <section aria-label={`Original (${name})`} className="min-w-0">
            <h4 className="m-0 mb-1.5 text-label font-semibold tracking-caps text-muted-foreground uppercase">
              Original · {name}
            </h4>
            <OriginalText original={original} language={language} renderOriginal={renderOriginal} />
          </section>
          <section aria-label="English machine translation" className="min-w-0 lg:border-l lg:pl-6">
            <h4 className="m-0 mb-1.5 text-label font-semibold tracking-caps text-muted-foreground uppercase">
              English · machine translation
            </h4>
            <EnglishText english={english} />
          </section>
        </div>
      ) : mode === "english" ? (
        <EnglishText english={english} />
      ) : (
        <OriginalText original={original} language={language} renderOriginal={renderOriginal} />
      )}
      <MachineTranslationNote />
    </div>
  );
}
